import axios, {AxiosResponse} from "axios";
import {PagedResult, SpringPagedResult} from "@/service/PagedResult";
import Order from "@/model/Order";
import {OrderType, WorklogType} from "@/enums";
import {OrderAssignmentRequest, OrderEmployeeAssignment} from "@/model/OrderEmployeeAssignment";

const PATH = "/v2/orders";
const PATH_V3 = "/v3/orders";
const ORDER_PAGE_SIZE = 250;
// The page size the worklogs are loaded with, the endpoint's default
const WORKLOG_PAGE_SIZE = 250;
// The order the available orders are offered in, as the removed /accounting/worklog/availableOrders returned them
const AVAILABLE_ORDER_TYPES = [OrderType.INV, OrderType.MOD, OrderType.DRY, OrderType.BEV, OrderType.MIS, OrderType.MFR];

class OrderServiceV2 {

  getOrder(orderId: number): Promise<AxiosResponse<Order>> {
    return axios.get(`${PATH}/${orderId}`);
  }

  getOrderV3(orderUuid: string): Promise<AxiosResponse<OrderV3Dto>> {
    return axios.get(`${PATH_V3}/${orderUuid}`);
  }

  /**
   * The orders active on the date that the caller may see, loading every page of GET /v3/orders. Mapped to
   * {@link Order}s addressed by the deprecated legacyId, grouped by order type (inventories first) and newest first
   * within each type.
   */
  findAvailableOrdersV3(date: string): Promise<Array<Order>> {
    return this.findOrdersActiveOnV3From(date, 0, [])
      .then(orders => orders
        .sort((a, b) => AVAILABLE_ORDER_TYPES.indexOf(a.orderType) - AVAILABLE_ORDER_TYPES.indexOf(b.orderType)
          || b.validFrom.localeCompare(a.validFrom))
        .map(order => Object.assign(new Order(), {
          id: order.legacyId,
          uuid: order.id,
          name: order.name,
          type: order.orderType,
          validFrom: order.validFrom,
          validTo: order.validTo ?? null
        })));
  }

  private findOrdersActiveOnV3From(date: string, page: number, orders: Array<OrderV3Dto>): Promise<Array<OrderV3Dto>> {
    return axios.get<SpringPagedResult<OrderV3Dto>>(PATH_V3, {
      params: {
        activeOn: date,
        page: page,
        size: ORDER_PAGE_SIZE
      }
    }).then(response => {
      const loaded = orders.concat(response.data.content);
      return page + 1 < response.data.page.totalPages
        ? this.findOrdersActiveOnV3From(date, page + 1, loaded)
        : loaded;
    });
  }

  updateOrder(orderId: number, data: unknown) {
    return axios.patch(`${PATH}/${orderId}`, data, {headers: {"Content-Type": "application/merge-patch+json"}});
  }

  deleteOrder(orderId: number) {
    return axios.delete(`${PATH}/${orderId}`);
  }

  findOrdersInTimeframe(from: string, to: string, include: string | null = null, orderType: OrderType | null = null): Promise<AxiosResponse<PagedResult>> {
    return axios.get(`${PATH}`, {
      params: {
        from: from,
        to: to,
        include: include,
        orderType: orderType,
        size: 1000
      }
    });
  }

  exportOrdersInTimeframe(from: string, to: string, orderType: OrderType | undefined = undefined): string {
    const baseUrl = `/api${PATH}/export?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    const orderTypeParam = orderType ? `&orderType=${encodeURIComponent(orderType)}` : "";
    return baseUrl + orderTypeParam;
  }
   

  // Worklogs, addressed by the order's uuid
  /**
   * The worklogs of an order via GET /v3/orders/{orderUuid}/worklogs, ordered by day, start time and employee name,
   * loading every page of the paged endpoint. Without `month` (`YYYY-MM`), the worklogs of every month; without
   * `worklogTypes`, those of every type.
   */
  findOrderWorklogsV3(orderUuid: string, month: string | null = null, worklogTypes: Array<WorklogType> = []): Promise<Array<OrderWorklogV3Dto>> {
    return this.findOrderWorklogsV3From(orderUuid, month, worklogTypes, 0, []);
  }

  private findOrderWorklogsV3From(orderUuid: string, month: string | null, worklogTypes: Array<WorklogType>, page: number,
                                  worklogs: Array<OrderWorklogV3Dto>): Promise<Array<OrderWorklogV3Dto>> {
    return axios.get<SpringPagedResult<OrderWorklogV3Dto>>(`${PATH_V3}/${orderUuid}/worklogs`, {
      params: {
        month: month ?? undefined,
        worklogTypes: worklogTypes,
        page: page,
        size: WORKLOG_PAGE_SIZE
      },
      // Repeats worklogTypes=WORK&worklogTypes=TRAVEL, as Spring binds it, instead of worklogTypes[]=…
      paramsSerializer: {indexes: null}
    }).then(response => {
      const loaded = worklogs.concat(response.data.content);
      return page + 1 < response.data.page.totalPages
        ? this.findOrderWorklogsV3From(orderUuid, month, worklogTypes, page + 1, loaded)
        : loaded;
    });
  }

  // Assignments, addressed by the order's and the employee's uuid
  getOrderAssignments(orderUuid: string): Promise<AxiosResponse<{ assignments: Array<OrderEmployeeAssignment> }>> {
    return axios.get(`${PATH}/${orderUuid}/assignments`);
  }

  updateOrderAssignment(orderUuid: string, employeeUuid: string, request: OrderAssignmentRequest): Promise<AxiosResponse<OrderEmployeeAssignment>> {
    return axios.put(`${PATH}/${orderUuid}/assignments/${employeeUuid}`, request);
  }

  deleteOrderAssignment(orderUuid: string, employeeUuid: string) {
    return axios.delete(`${PATH}/${orderUuid}/assignments/${employeeUuid}`);
  }

  // Checklist of an inventory, addressed by the order's uuid
  getOrderChecklist(orderUuid: string): Promise<AxiosResponse<OrderChecklistResponse>> {
    return axios.get(`${PATH}/${orderUuid}/checklist`);
  }

  /**
   * Completes the item now, by the caller. Completing an item that is already completed keeps when and by whom it was
   * completed.
   */
  completeChecklistItem(orderUuid: string, code: string): Promise<AxiosResponse<ChecklistItemDto>> {
    return axios.post(`${PATH}/${orderUuid}/checklist/${code}/complete`);
  }

  // Per diems, addressed by the order's and the employees' uuids
  /**
   * The days of a month on which employees logged time on the order, with their per diems. Without `month`, the
   * month the order's per diems were most recently recorded in; the response's `month` tells which one.
   */
  getOrderPerDiems(orderUuid: string, month: string | null = null): Promise<AxiosResponse<OrderPerDiemsResponse>> {
    return axios.get(`${PATH}/${orderUuid}/per-diems`, {
      params: month ? {month: month} : {}
    });
  }

  /**
   * Gives several employees the same per diems on one day, recorded for the order. Both amounts 0 without breakfast
   * deletes their per diems on that day instead. Applied to all employees or, if any of them is rejected, to none.
   */
  updateOrderPerDiems(orderUuid: string, request: OrderPerDiemsBulkRequest): Promise<AxiosResponse<void>> {
    return axios.post(`${PATH}/${orderUuid}/per-diems/_bulk`, request);
  }
}

/**
 * An order as the v3 api (GET /v3/orders/{orderUuid}) returns it. `id` is the order's uuid.
 */
export interface OrderV3Dto {
  id: string;
  /** @deprecated The order's numeric id, only provided for backward compatibility */
  legacyId?: number;
  name: string;
  orderType: OrderType;
  validFrom: string;
  validTo?: string;
}

/**
 * One worklog of GET /v3/orders/{orderId}/worklogs. `id` and the employee's `id` are uuids, `start` and `end` are times
 * of day in the form `HH:mm` and `minutes` is the time between them. `createdBy` and `createdAt` tell who recorded the
 * worklog and when; clocking out or changing a worklog replaces it, so they belong to its latest change.
 */
export interface OrderWorklogV3Dto {
  id: string;
  employee: {
    id: string;
    employeeNumber: number;
    firstName: string;
    lastName: string;
  };
  date: string;
  start: string;
  end: string;
  minutes: number;
  type: WorklogType;
  comment: string | null;
  createdBy: string;
  createdAt: string;
}

/**
 * An item of the checklist of an inventory. `label` is in the language `Accept-Language` asks for.
 */
export interface ChecklistItemDto {
  code: string;
  phase: "BEFORE" | "DURING" | "AFTER";
  label: string;
  completed?: boolean;
  completedAt?: string;
  completedBy?: string;
}

/**
 * The checklist of an inventory, its items in the order they are worked through.
 */
export interface OrderChecklistResponse {
  items: Array<ChecklistItemDto>;
}

/**
 * The per diem of an employee on one day as the v2 order API returns it. Its `id` is a uuid and the order is
 * referenced by its uuid and name only; it may be recorded for another order than the one asked for.
 */
export interface OrderPerDiemDto {
  id: string;
  order: { id: string; name: string };
  catering: number;
  misc: number;
  breakfast?: boolean;
  cateringAfterBreakfast: number;
}

/**
 * An employee who logged time on the order on one day, with the minutes per worklog type and their per diem of that
 * day, if any.
 */
export interface OrderPerDiemEmployeeDayDto {
  employee: { id: string; employeeNumber?: number; firstName: string; lastName: string };
  workMinutes?: number;
  travelMinutes?: number;
  passengerMinutes?: number;
  perDiem?: OrderPerDiemDto;
}

export interface OrderPerDiemDayDto {
  date: string;
  editable?: boolean;
  employees: Array<OrderPerDiemEmployeeDayDto>;
}

/**
 * The per diems of an order in one month. `days` only lists days on which time was logged, oldest first.
 */
export interface OrderPerDiemsResponse {
  month: string;
  days: Array<OrderPerDiemDayDto>;
}

/**
 * The per diems to give several employees of an order on one day.
 */
export interface OrderPerDiemsBulkRequest {
  date: string;
  employeeIds: Array<string>;
  catering: number;
  misc: number;
  breakfast: boolean;
}

export default new OrderServiceV2();
